import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Price, Tabs } from '@artosera/ui';
import { useTableauPaiement, useEncaissementsPaiement } from '../../api/paiementEnLigne.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import {
  EncaissementsPaiement,
  ErreurPaiement,
  PagesPaiement,
  SuspensionPaiement,
  useTextePaiement,
} from './paiementUi.js';
import { Relances } from './Relances.js';
import { Reglages } from './Reglages.js';
import styles from './PaiementEnLigne.module.css';

export function PaiementEnLignePage() {
  const { galerie, galerieSlug } = useShellContext();
  const { label } = useTextePaiement();
  return (
    <div data-ui="page" className={styles.page}>
      <h1>{label('titre')}</h1>
      <Tabs
        tabs={[
          { value: 'impayes', label: label('impayes'), content: <Impayes /> },
          { value: 'relances', label: label('relances'), content: <Relances /> },
          {
            value: 'encaissements',
            label: label('encaissements'),
            content: <JournalEncaissements />,
          },
          ...(galerieALaction(galerie, 'online-payments:manage')
            ? [
                {
                  value: 'reglages',
                  label: label('reglages'),
                  content: <Reglages galerieSlug={galerieSlug} />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}
function Impayes() {
  const { galerie, galerieSlug } = useShellContext();
  const { t, label, langue } = useTextePaiement();
  const [page, setPage] = useState(1);
  const lecture = useTableauPaiement(galerieSlug, { page });
  return (
    <section className={styles.section}>
      <h2>{label('impayes')}</h2>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      <ErreurPaiement erreur={lecture.error} />
      {lecture.data && (
        <>
          <p>
            {label('aRegulariser')} : {lecture.data.aRegulariser} · {label('aEnvoyer')} :{' '}
            {lecture.data.relancesAEnvoyer}
          </p>
          <dl className={styles.totaux}>
            {lecture.data.impayesParPalier.map((p) => (
              <div key={p.rang}>
                <dt>
                  {label('palier')} {p.rang} · {p.nombre}
                </dt>
                <dd>
                  <Price cents={p.resteDuCentimes} lang={langue} />
                </dd>
              </div>
            ))}
          </dl>
          <div className={styles.tableauEnveloppe}>
            <table className={styles.tableau}>
              <thead>
                <tr>
                  {['facture', 'palier', 'resteDu', 'echeance', 'relances'].map((c) => (
                    <th scope="col" key={c}>
                      {label(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lecture.data.impayes.map((f) => (
                  <tr key={f.factureId}>
                    <td>
                      <Link to={`/${galerieSlug}/ventes/${f.factureId}`}>{f.numero}</Link>
                    </td>
                    <td>{f.rangPalier ?? '—'}</td>
                    <td>
                      <Price cents={f.resteDuCentimes} lang={langue} />
                    </td>
                    <td>{f.echeanceLe?.slice(0, 10) ?? '—'}</td>
                    <td>
                      {f.relancesSuspendues && label('suspendues')}
                      {galerieALaction(galerie, 'online-payments:write') && (
                        <SuspensionPaiement
                          galerieSlug={galerieSlug}
                          factureId={f.factureId}
                          suspendues={f.relancesSuspendues}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PagesPaiement pagination={lecture.data.pagination} changer={setPage} />
        </>
      )}
    </section>
  );
}
function JournalEncaissements() {
  const { galerieSlug } = useShellContext();
  const { t, label } = useTextePaiement();
  const [page, setPage] = useState(1);
  const [regulariser, setRegulariser] = useState(false);
  const lecture = useEncaissementsPaiement(galerieSlug, {
    page,
    ...(regulariser ? { etat: 'a_regulariser' as const } : {}),
  });
  return (
    <section className={styles.section}>
      <h2>{label('encaissements')}</h2>
      <div className={styles.actions}>
        <Button
          aria-pressed={!regulariser}
          onClick={() => {
            setPage(1);
            setRegulariser(false);
          }}
        >
          {label('recents')}
        </Button>
        <Button
          aria-pressed={regulariser}
          onClick={() => {
            setPage(1);
            setRegulariser(true);
          }}
        >
          {label('aRegulariser')}
        </Button>
      </div>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      <ErreurPaiement erreur={lecture.error} />
      {lecture.data && (
        <>
          <EncaissementsPaiement
            galerieSlug={galerieSlug}
            encaissements={lecture.data.encaissements}
          />
          <PagesPaiement pagination={lecture.data.pagination} changer={setPage} />
        </>
      )}
    </section>
  );
}
